import type { Messages } from './messages';

/**
 * Copy for `src/app/dashboard/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `dashboard.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 *
 * Counts are written as «label: N» rather than «N things», so no sentence here
 * depends on Arabic number agreement (مستند / مستندان / مستندات / مستندًا).
 */
export const dashboard = {
  'dashboard.title': 'الرئيسية',
  'dashboard.greetingMorning': 'صباح الخير، {name}',
  'dashboard.greetingEvening': 'مساء الخير، {name}',
  'dashboard.subtitle': 'حال المنصّة في {month}. الأشهر والأسابيع بتوقيت الرياض.',
  'dashboard.subtitleLoading': 'حال المنصّة الآن.',
  'dashboard.updated': 'آخر تحديث {when}',
  'dashboard.refresh': 'تحديث',
  'dashboard.newTenant': 'منشأة جديدة',
  'dashboard.loadFailed': 'تعذّر تحميل مؤشرات المنصّة',
  'dashboard.staleTitle': 'تعذّر التحديث — المعروض من آخر تحميل ناجح.',

  /* KPI strip */
  'dashboard.kpis': 'المؤشرات الرئيسية',
  'dashboard.kpiActiveTenants': 'المنشآت النشطة',
  'dashboard.kpiActiveTenantsSub': 'من أصل {total} · موقوفة: {suspended}',
  'dashboard.kpiNewTenants': 'جديدة في آخر 30 يومًا: {count}',
  'dashboard.kpiAccounts': 'الحسابات',
  'dashboard.kpiAccountsSub': 'نشطة: {active} · بانتظار التفعيل: {pending}',
  'dashboard.kpiSpend': 'الإنفاق هذا الشهر',
  'dashboard.kpiSpendSub': 'كل المنشآت · {month}',
  'dashboard.kpiDocuments': 'المستندات الجاهزة',
  'dashboard.kpiDocumentsFailed': 'متعثّرة: {count}',
  'dashboard.kpiDocumentsNoneFailed': 'لا مستندات متعثّرة',
  'dashboard.kpiCases': 'القضايا النشطة',
  'dashboard.kpiCasesSub': 'في كل المنشآت',
  'dashboard.kpiPerTenant': 'متوسط المنشأة النشطة: {count}',
  'dashboard.kpiNearCap': '{count} منشأة عند {percent} من سقفها أو فوقه',
  'dashboard.kpiNoneNearCap': 'لا منشأة قرب سقفها',
  'dashboard.kpiFailedShare': 'نسبة التعثّر: {percent}',
  'dashboard.weeklySpark': 'المنشآت المُنشأة في آخر ثمانية أسابيع: من {first} إلى {last} أسبوعيًا.',

  /* Attention */
  'dashboard.attention': 'تحتاج انتباهًا',
  'dashboard.attentionDescription': 'منشآت فيها ما ينتظر قرارًا أو إصلاحًا، الأشد إلحاحًا أولًا.',
  'dashboard.attentionFilter': 'تصفية حسب النوع',
  'dashboard.attentionAll': 'الكل',
  'dashboard.attentionShowAll': 'عرض الكل ({count})',
  'dashboard.attentionShowLess': 'عرض أقل',
  'dashboard.attentionCapped': 'تُعرض أول {count} حالة فقط؛ عالج هذه لتظهر البقية.',
  'dashboard.attentionEmptyTitle': 'لا شيء ينتظرك',
  'dashboard.attentionEmptyBody':
    'لا مستندات متعثّرة، ولا إنفاق قرب سقفه، ولا منشأة بلا مشرف عام، ولا طلبات انضمام معلّقة.',
  'dashboard.attentionOpen': 'افتح {tab}',
  'dashboard.explainDocumentsFailed': 'مستندات فشلت معالجتها: {count}. لم تُفهرس، فلن يجدها البحث.',
  'dashboard.explainSpendNear': 'أُنفق {percent} من السقف الشهري.',
  'dashboard.explainSpendOver': 'تجاوز الإنفاق السقف الشهري: {percent} منه.',
  'dashboard.explainNoAdminNone': 'لا مشرف عام نشط ولا دعوة معلّقة — لا أحد يستطيع إدارة المنشأة من داخلها.',
  'dashboard.explainNoAdminInvited': 'لا مشرف عام نشط بعد. دعوات لم تُقبل: {count} — أعد إرسالها أو تحقّق من العنوان.',
  'dashboard.explainJoinWaiting': 'طلبات انضمام بانتظار القبول أو الرفض: {count}.',
  'dashboard.kindDocuments': 'مستندات',
  'dashboard.kindSpend': 'الإنفاق',
  'dashboard.kindAdmin': 'المشرف العام',
  'dashboard.kindJoin': 'طلبات الانضمام',

  /* Trend */
  'dashboard.weekly': 'المنشآت الجديدة أسبوعيًا',
  'dashboard.weeklyDescription': 'آخر ثمانية أسابيع؛ يبدأ الأسبوع يوم الإثنين.',
  'dashboard.weeklyAlt': 'عدد المنشآت المُنشأة في كل أسبوع من الأسابيع الثمانية الأخيرة.',
  'dashboard.weeklyWeekOf': 'أسبوع {day}',
  'dashboard.weeklyFootnote': 'هذا الأسبوع: {week} · آخر 30 يومًا: {month}',
  'dashboard.weeklyEmpty': 'لم تُنشأ أي منشأة في الأسابيع الثمانية الأخيرة.',

  /* Spend */
  'dashboard.spend': 'الإنفاق مقابل السقف',
  'dashboard.spendDescription': 'أعلى المنشآت إنفاقًا في {month}. العلامة العمودية هي السقف الشهري.',
  'dashboard.spendAlt': 'إنفاق أعلى المنشآت هذا الشهر مقارنة بسقف كل منها.',
  'dashboard.spendOfCap': 'من {cap}',
  'dashboard.spendNoCap': 'بلا سقف',
  'dashboard.spendWithin': 'ضمن السقف',
  'dashboard.spendOpen': 'افتح نماذج {name}',
  'dashboard.spendFootnote': 'إجمالي إنفاق المنصّة في {month}: {total}',
  'dashboard.spendEmpty': 'لم تُسجَّل أي تكلفة هذا الشهر بعد. يظهر هنا الإنفاق حين تبدأ المنشآت باستخدام النماذج.',

  /* Verticals */
  'dashboard.verticals': 'المنشآت حسب النشاط',
  'dashboard.verticalsAlt': 'توزيع المنشآت على الأنشطة.',
  'dashboard.verticalsCenter': 'منشأة',
  'dashboard.verticalsOther': 'أخرى',

  /* Content */
  'dashboard.content': 'المحتوى عبر المنشآت',
  'dashboard.contentDescription': 'أعداد فقط، مجمّعة من كل المنشآت.',
  'dashboard.conversations': 'المحادثات',
  'dashboard.projects': 'المشاريع',
  'dashboard.casesActive': 'القضايا النشطة',
  'dashboard.documentsReady': 'المستندات الجاهزة',
  'dashboard.documentsFailed': 'المستندات المتعثّرة',
  'dashboard.documentsFailedShare': 'نسبة التعثّر: {percent}',

  /* First run */
  'dashboard.firstRunTitle': 'لا منشآت على المنصّة بعد',
  'dashboard.firstRunBody':
    'ابدأ بأول منشأة: اسمها ونشاطها ومشرفها العام. حين تُنشأ تظهر هنا حساباتها وإنفاقها وما يحتاج انتباهك فيها.',
  'dashboard.firstRunNoCreate': 'رتبتك لا تسمح بإنشاء منشأة. اطلب من مشغّل برتبة أعلى أن يُنشئ الأولى.',
  'dashboard.firstRunAction': 'إنشاء أول منشأة',
} as const satisfies Messages<'dashboard'>;
